import { AxiosStatic } from "axios";
import { flatten, splitEvery } from "ramda";

import { IResolveResponse, ResolvableType } from "../../api/ResolveApi/resolve-api.types";
import { ICourseChange, IFullCourse } from "../../components/course-author/types";
import { hasErrorRecords, IClassResponse } from "../../parsers/resolve";
import { AxiosInterceptedResult } from "../interceptors";
import { GenericResolveFn, IDisplayInfoResponse, IOrgDisplayData, IResolveApiDependencies } from "./resolve-api.types";

export interface IResolveApi {
  readonly account: {
    readonly post: GenericResolveFn<
      AxiosInterceptedResult<Array<IResolveResponse<{ state: { displayName: string } }>>>
    >;
  };
  readonly class: {
    readonly post: GenericResolveFn<AxiosInterceptedResult<Array<IResolveResponse<IClassResponse>>>>;
  };
  readonly course: {
    readonly post: GenericResolveFn<AxiosInterceptedResult<Array<IResolveResponse<{ courseTitle: string }>>>>;
  };
  readonly courseChanges: {
    readonly post: GenericResolveFn<AxiosInterceptedResult<ICourseChange[]>>;
  };
  readonly courseFull: {
    readonly post: GenericResolveFn<AxiosInterceptedResult<IFullCourse>>;
  };
  readonly courseInstance: { readonly post: GenericResolveFn };
  readonly freshCourseInstance: { readonly post: GenericResolveFn };
  readonly organization: { readonly post: GenericResolveFn };
  readonly organizationDisplay: {
    readonly post: GenericResolveFn;
  };
  readonly scormPackage: { readonly post: GenericResolveFn };
  readonly style: { readonly post: GenericResolveFn };
  readonly displayInfo: {
    readonly post: GenericResolveFn<AxiosInterceptedResult<Array<IResolveResponse<IDisplayInfoResponse> | null>>>;
  };
}

export const GenericErrorMsg = "Error on the response data";

export function getErrorIfExists<T>(responses: Array<AxiosInterceptedResult<T>>): string | null {
  // Note: the API could respond 'OK' but the response data may contain an error
  // so we have to find that error if it exists.
  const errors = responses.find(
    ([err, resp]) => err !== null || (resp !== null && Array.isArray(resp.data) && hasErrorRecords(resp.data))
  );
  return errors !== undefined ? errors[0] || GenericErrorMsg : null;
}

interface IGenericResolveOpts {
  includeErrors: boolean;
}

function genericResolve<T = any>(
  axios: AxiosStatic,
  apiRoot: string,
  t: ResolvableType,
  suffix: string = "",
  options: IGenericResolveOpts = {
    includeErrors: false,
  }
): GenericResolveFn<AxiosInterceptedResult<Array<T | null>>> {
  return (
    activeUserUuid: string,
    uuids: Array<[ResolvableType, string] | string>,
    activeOrganizationId = undefined
  ) => {
    const slices = splitEvery(50, uuids);
    const config = {
      params: { activeOrg: activeOrganizationId, activeUser: activeUserUuid },
    };
    const results: Array<Promise<AxiosInterceptedResult<T>>> = slices.map(s =>
      axios.post(
        `${apiRoot}/bulk/resolve`,
        s.map(u => (u instanceof Array ? `${u[0]}.${u[1]}${suffix}` : `${t}.${u}${suffix}`)),
        config
      )
    );
    return Promise.all(results).then((responses: Array<AxiosInterceptedResult<T>>) => {
      const error = getErrorIfExists<T>(responses);
      if (error !== null && !options.includeErrors) {
        return [error, null];
      }
      return [
        null,
        {
          config,
          data: flatten(responses.map(([e, r]) => (r === null ? null : r.data))),
          headers: {},
          status: 200,
          statusText: "OK",
        },
      ];
    });
  };
}

export default class ResolveApi implements IResolveApi {
  public readonly account: { readonly post: GenericResolveFn };
  public readonly class: { readonly post: GenericResolveFn };
  public readonly course: { readonly post: GenericResolveFn };
  public readonly courseChanges: { readonly post: GenericResolveFn };
  public readonly courseFull: { readonly post: GenericResolveFn };
  public readonly courseInstance: { readonly post: GenericResolveFn };
  public readonly freshCourseInstance: { readonly post: GenericResolveFn };
  public readonly organization: { readonly post: GenericResolveFn };
  public readonly organizationDisplay: {
    readonly post: GenericResolveFn;
  };
  public readonly scormPackage: { readonly post: GenericResolveFn };
  public readonly style: { readonly post: GenericResolveFn };
  public readonly displayInfo: {
    readonly post: GenericResolveFn<AxiosInterceptedResult<Array<IResolveResponse<IDisplayInfoResponse> | null>>>;
  };
  private readonly apiRoot: string;
  private readonly axios: AxiosStatic;

  constructor(dependencies: IResolveApiDependencies) {
    this.apiRoot = dependencies.saltcomplianceApiRoot;
    this.axios = dependencies.axios;
    this.account = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.Account, "?"),
    };
    this.class = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.Class, "?"),
    };
    this.course = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.Course, "?summary", { includeErrors: true }),
    };
    this.courseChanges = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.Course),
    };
    this.courseFull = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.Course, "?"),
    };
    this.courseInstance = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.CourseInstance, "?summary"),
    };
    this.freshCourseInstance = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.CourseInstance, "?fresh_summary"),
    };
    this.organization = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.Organization, "?"),
    };
    this.organizationDisplay = {
      post: genericResolve<IOrgDisplayData[]>(this.axios, this.apiRoot, ResolvableType.Organization, "?display"),
    };
    this.scormPackage = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.ScormPackage, "?"),
    };
    this.style = {
      post: genericResolve(this.axios, this.apiRoot, ResolvableType.Style, "?", { includeErrors: true }),
    };
    this.displayInfo = {
      post: genericResolve(
        this.axios,
        this.apiRoot,
        "" as ResolvableType, // This value isn't actually used
        "?display"
      ),
    };
  }
}
